import { useState } from "react";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@bednbody/ui/components/button";
import { Input } from "@bednbody/ui/components/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@bednbody/ui/components/input-otp";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@bednbody/ui/components/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";

const passwordSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

const mfaSchema = z.object({
  code: z
    .string()
    .length(6, "Must be 6 digits")
    .regex(/^\d{6}$/, "Numbers only"),
});

type PasswordForm = z.infer<typeof passwordSchema>;
type MfaForm = z.infer<typeof mfaSchema>;

export function Login() {
  const { signInWithPassword, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: Location })?.from?.pathname ?? "/";

  const [factorId, setFactorId] = useState<string | null>(null);
  const [setup, setSetup] = useState<{ qrCode: string; secret: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { email: "", password: "" },
  });

  const mfaForm = useForm<MfaForm>({
    resolver: zodResolver(mfaSchema),
    defaultValues: { code: "" },
  });

  async function onPasswordSubmit(data: PasswordForm) {
    setError(null);
    setBusy(true);
    const result = await signInWithPassword(data.email, data.password);
    setBusy(false);

    if (result.status === "error") {
      setError(result.message);
    } else if (result.status === "mfa_required") {
      setFactorId(result.factorId);
    } else if (result.status === "mfa_setup") {
      setFactorId(result.factorId);
      setSetup({ qrCode: result.qrCode, secret: result.secret });
    } else {
      navigate(from, { replace: true });
    }
  }

  async function onMfaSubmit(data: MfaForm) {
    if (!factorId) return;
    setError(null);
    setBusy(true);
    const { error: mfaError } = await verifyMfa(factorId, data.code);
    setBusy(false);

    if (mfaError) {
      setError(mfaError);
    } else {
      navigate(from, { replace: true });
    }
  }

  const description = setup
    ? "Set up two-factor authentication"
    : factorId
      ? "Enter your authenticator code"
      : "Staff sign in";

  return (
    <div className="flex flex-col h-full items-center justify-center">
      <Card className="w-sm shadow-none ring-transparent">
        <CardHeader>
          <CardTitle>Bednbody Admin</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>

        <CardContent>
          {setup ? (
            <div className="mb-4 flex flex-col items-center gap-2 rounded-lg border border-border bg-background p-4">
              <img src={setup.qrCode} alt="TOTP enrollment QR code" className="h-40 w-40" />
              <p className="text-center text-xs text-muted-foreground">
                Scan with your authenticator app, or enter this key manually:
              </p>
              <code className="break-all rounded bg-card px-2 py-1 text-xs">{setup.secret}</code>
            </div>
          ) : null}

          {!factorId ? (
            <Form {...passwordForm}>
              <form
                onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
                className="flex flex-col gap-4"
              >
                <FormField
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" autoComplete="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <div className="relative">
                        <FormControl>
                          <Input
                            className="pe-9"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            {...field}
                          />
                        </FormControl>
                        <button
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          aria-pressed={showPassword}
                          className="absolute inset-y-0 inset-e-0 flex h-full w-9 items-center justify-center rounded-e-md text-muted-foreground/80 outline-none transition-[color,box-shadow] hover:text-foreground focus:z-10 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => setShowPassword((v) => !v)}
                          type="button"
                        >
                          {showPassword ? (
                            <EyeSlashIcon weight="fill" size={16} />
                          ) : (
                            <EyeIcon weight="fill" size={16} />
                          )}
                        </button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {error ? <p className="text-sm text-destructive">{error}</p> : null}
                <Button type="submit" disabled={busy}>
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </Form>
          ) : (
            <Form {...mfaForm}>
              <form onSubmit={mfaForm.handleSubmit(onMfaSubmit)} className="flex flex-col gap-4">
                <Controller
                  name="code"
                  control={mfaForm.control}
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-1 mx-auto">
                      <label className="text-sm">6-digit code</label>
                      <InputOTP
                        maxLength={6}
                        value={field.value}
                        onChange={field.onChange}
                        pattern={REGEXP_ONLY_DIGITS}
                        onComplete={() => mfaForm.handleSubmit(onMfaSubmit)()}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                      {fieldState.error && (
                        <p className="text-sm text-destructive">{fieldState.error.message}</p>
                      )}
                    </div>
                  )}
                />

                {error ? <p className="text-sm text-destructive">{error}</p> : null}
                <Button type="submit" disabled={busy}>
                  {busy ? "Verifying…" : "Verify"}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
